import {
  checkIngestionStatus,
  createKnowledgeBase,
  deleteDocument,
  deleteKnowledgeBase,
  fetchAllKnowledgeBases,
  fetchKnowledgeBaseById,
  getFilePreviewUrl,
  syncKnowledgeBase,
  updateKnowledgeBase,
  uploadDocument,
} from '@/services/knowledge-base.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { PaginatedResponse, PaginationParams, QueryOptions } from '@/types/common.type';
import type {
  DeleteKnowledgeBaseResponse,
  KnowledgeBase,
  KnowledgeBaseCreateRequest,
  KnowledgeBaseDetail,
  KnowledgeBaseSyncStatus,
  KnowledgeBaseUpdateRequest,
} from '@/types/knowledge.type';
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

const getKnowledgeBaseListQueries = (queryClient: QueryClient) => {
  return queryClient.getQueriesData<PaginatedResponse<KnowledgeBase>>({
    queryKey: queryKeys.knowledgeBases.all,
  });
};

const removeKnowledgeBaseFromCachedLists = (queryClient: QueryClient, kbId: number) => {
  queryClient.setQueriesData(
    { queryKey: queryKeys.knowledgeBases.all },
    (old: PaginatedResponse<KnowledgeBase> | undefined) => {
      if (!old) return old;
      return {
        ...old,
        data: old.data.filter(kb => kb.kb_id !== kbId),
      };
    }
  );
};

/**
 * Query hook to fetch all knowledge bases with pagination
 */
export const useKnowledgeBasesQuery = (
  params: PaginationParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<KnowledgeBase>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.knowledgeBases.list(params.page || 1, params.page_size, params.query || ''),
    queryFn: () => fetchAllKnowledgeBases(params),
    ...options,
  });
};

/**
 * Query hook to fetch a single knowledge base by ID
 */
export const useKnowledgeBaseDetailQuery = (
  kbId: number,
  options?: Omit<UseQueryOptions<KnowledgeBaseDetail, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.knowledgeBases.detail(kbId),
    queryFn: () => fetchKnowledgeBaseById(kbId),
    ...options,
  });
};

/**
 * Query hook to check the sync/ingestion status of a knowledge base
 */
export const useKnowledgeBaseSyncStatusQuery = (
  kbId: number,
  options?: Omit<UseQueryOptions<KnowledgeBaseSyncStatus, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.knowledgeBases.syncStatus(kbId),
    queryFn: () => checkIngestionStatus(kbId),
    ...options,
  });
};

/**
 * Query hook to get a file preview URL
 */
export const useFilePreviewUrlQuery = (
  filePath: string,
  enabled = true,
  options?: Omit<UseQueryOptions<string, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.knowledgeBases.filePreview(filePath),
    queryFn: () => getFilePreviewUrl(filePath),
    enabled,
    ...options,
  });
};

/**
 * Mutation hook to create a new knowledge base
 */
export const useCreateKnowledgeBaseMutation = (options?: QueryOptions<KnowledgeBase>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.knowledgeBases.mutations.create,
    mutationFn: (payload: KnowledgeBaseCreateRequest) => createKnowledgeBase(payload),
    onSuccess: data => {
      notifyMutationSuccessWithOptions(
        options,
        'Knowledge base created',
        'A new knowledge base was created successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases.all });
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Knowledge base creation failed', error);
      options?.onError?.(error);
    },
  });
};

/**
 * Mutation hook to update a knowledge base
 */
export const useUpdateKnowledgeBaseMutation = (options?: QueryOptions<KnowledgeBaseDetail>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.knowledgeBases.mutations.update,
    mutationFn: ({ kbId, payload }: { kbId: number; payload: KnowledgeBaseUpdateRequest }) =>
      updateKnowledgeBase(kbId, payload),
    onSuccess: (data, variables) => {
      notifyMutationSuccessWithOptions(
        options,
        'Knowledge base updated',
        'Knowledge base was updated successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases.detail(variables.kbId) });
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Knowledge base update failed', error);
      options?.onError?.(error);
    },
  });
};

/**
 * Mutation hook to delete a knowledge base
 */
export const useDeleteKnowledgeBaseMutation = (
  options?: QueryOptions<DeleteKnowledgeBaseResponse>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.knowledgeBases.mutations.delete,
    mutationFn: (kbId: number) => deleteKnowledgeBase(kbId),
    onMutate: async kbId => {
      await queryClient.cancelQueries({ queryKey: queryKeys.knowledgeBases.all });
      const previousData = getKnowledgeBaseListQueries(queryClient);

      removeKnowledgeBaseFromCachedLists(queryClient, kbId);

      return { previousData };
    },
    onError: (error, _variables, onMutateResult) => {
      onMutateResult?.previousData?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      notifyMutationErrorWithOptions(options, 'Knowledge base deletion failed', error);
      options?.onError?.(error);
    },
    onSuccess: (data, kbId) => {
      if (data.accepted) {
        notifyMutationSuccessWithOptions(
          options,
          'Knowledge base deletion started',
          'The knowledge base deletion request was accepted and will complete shortly.'
        );

        removeKnowledgeBaseFromCachedLists(queryClient, kbId);
      } else {
        notifyMutationSuccessWithOptions(
          options,
          'Knowledge base deleted',
          'The knowledge base was deleted successfully.'
        );

        queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases.all });
      }

      queryClient.removeQueries({ queryKey: queryKeys.knowledgeBases.detail(kbId) });
      options?.onSuccess?.(data);
    },
  });
};

/**
 * Mutation hook to upload a document to a knowledge base
 */
export const useUploadDocumentMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.knowledgeBases.mutations.uploadDocument,
    mutationFn: ({ kbId, file }: { kbId: number; file: File }) => uploadDocument(kbId, file),
    onSuccess: (_data, variables) => {
      notifyMutationSuccessWithOptions(
        options,
        'Document uploaded',
        'Document was uploaded successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases.detail(variables.kbId) });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Document upload failed', error);
      options?.onError?.(error);
    },
  });
};

/**
 * Mutation hook to delete a document from a knowledge base
 */
export const useDeleteDocumentMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.knowledgeBases.mutations.deleteDocument,
    mutationFn: ({ kbId, filePath }: { kbId: number; filePath: string }) =>
      deleteDocument(kbId, filePath),
    onMutate: async ({ kbId, filePath }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.knowledgeBases.detail(kbId) });

      const previousData = queryClient.getQueryData<KnowledgeBaseDetail>(
        queryKeys.knowledgeBases.detail(kbId)
      );

      queryClient.setQueryData<KnowledgeBaseDetail>(
        queryKeys.knowledgeBases.detail(kbId),
        currentData => {
          if (!currentData) return currentData;

          return {
            ...currentData,
            file_list: currentData.file_list.filter(file => file.file_path !== filePath),
          };
        }
      );

      return { previousData };
    },
    onError: (error, variables, onMutateResult) => {
      if (onMutateResult?.previousData) {
        queryClient.setQueryData(
          queryKeys.knowledgeBases.detail(variables.kbId),
          onMutateResult.previousData
        );
      }

      notifyMutationErrorWithOptions(options, 'Document deletion failed', error);
      options?.onError?.(error);
    },
    onSuccess: (_data, variables) => {
      notifyMutationSuccessWithOptions(
        options,
        'Document deleted',
        'The document was deleted successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases.detail(variables.kbId) });
      options?.onSuccess?.();
    },
  });
};

/**
 * Mutation hook to sync a knowledge base
 */
export const useSyncKnowledgeBaseMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.knowledgeBases.mutations.sync,
    mutationFn: (kbId: number) => syncKnowledgeBase(kbId),
    onSuccess: (_data, kbId) => {
      notifyMutationSuccessWithOptions(
        options,
        'Knowledge base sync started',
        'The knowledge base sync has been initiated.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases.syncStatus(kbId) });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Knowledge base sync failed', error);
      options?.onError?.(error);
    },
  });
};
